/**
 * DropZone のサムネイル関連コンポーネント (300 行制限対応で分離)。
 */

import { Image as ImageIcon, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '../../lib/utils';

interface GridProps {
  readonly files: readonly File[];
  readonly heroIndex: number;
  readonly disabled: boolean;
  readonly onHeroChange: (index: number) => void;
  readonly onFileRemoved: (index: number) => void;
}

export function ThumbnailGrid(props: GridProps): JSX.Element {
  return (
    <ul className="grid max-h-80 grid-cols-6 gap-2 overflow-y-auto sm:grid-cols-8">
      {props.files.map((file, i) => (
        <li key={`${file.name}-${file.size}-${i}`}>
          <Thumbnail
            file={file}
            index={i}
            isHero={i === props.heroIndex}
            disabled={props.disabled}
            onSelect={() => props.onHeroChange(i)}
            onRemove={() => props.onFileRemoved(i)}
          />
        </li>
      ))}
    </ul>
  );
}

interface ThumbnailProps {
  readonly file: File;
  readonly index: number;
  readonly isHero: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
  readonly onRemove: () => void;
}

/**
 * 1 枚のプレビュー。ObjectURL を createObjectURL / revokeObjectURL でライフサイクル管理。
 * Wave 22: 右上に × ボタン追加 (個別削除)。
 */
function Thumbnail(props: ThumbnailProps): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(props.file);
    setUrl(objectUrl);
    return (): void => URL.revokeObjectURL(objectUrl);
  }, [props.file]);

  return (
    <div
      className={cn(
        'group relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-sm border text-[10px] transition-colors',
        props.isHero
          ? 'border-accent-primary border-2 bg-[rgba(10,37,64,0.05)]'
          : 'border-silver-primary bg-bg-secondary hover:border-accent-primary',
      )}
    >
      <button
        type="button"
        disabled={props.disabled}
        onClick={props.onSelect}
        className="absolute inset-0 flex items-center justify-center"
        aria-label={`${props.file.name} をヒーロー写真にする`}
        title={props.file.name}
      >
        {url ? (
          <img
            src={url}
            alt={props.file.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <ImageIcon
            className="text-text-tertiary group-hover:text-accent-primary h-4 w-4"
            strokeWidth={1.5}
            aria-hidden
          />
        )}
      </button>
      {props.isHero ? (
        <span className="bg-accent-primary text-text-inverse pointer-events-none absolute right-0 bottom-0 left-0 px-1 py-0.5 text-[9px] font-medium">
          HERO
        </span>
      ) : null}
      <span className="bg-bg-primary/90 text-text-secondary pointer-events-none absolute top-0.5 left-0.5 rounded px-1 tabular-nums">
        {props.index + 1}
      </span>
      <button
        type="button"
        disabled={props.disabled}
        onClick={(e) => {
          e.stopPropagation();
          props.onRemove();
        }}
        className="bg-bg-primary/90 text-text-secondary hover:bg-status-danger hover:text-text-inverse absolute top-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full transition-colors"
        aria-label={`${props.file.name} を削除`}
        title="削除"
      >
        <X className="h-3 w-3" strokeWidth={2.5} aria-hidden />
      </button>
    </div>
  );
}
